import axios from 'axios';
import { BACKEND_URL } from '../config_and_helpers/config';
import { logError, logInfo } from '../config_and_helpers/logger';
import {
  finalizeQuotePdfUploadStatus,
  uploadQuoteExcelsToS3,
} from './quote_client';
import { parseApiErrorResponse } from '../utils/apiErrorMessages';
import { readCsvFileAsText } from '../utils/orderConfirmation/readCsvFileAsText';
import { logOrderConfirmationExtraction } from '../utils/orderConfirmation/logOrderConfirmationExtraction';
import { parseSigmaAldrichOrderConfirmationCsv } from '../utils/orderConfirmation/sigmaAldrichOrderConfirmationCsvParser';

const LOG_CONTEXT = 'order_confirmation_extraction_client';

const MAX_CSV_BYTES = 5 * 1024 * 1024;

/**
 * @param {string} fileName
 * @returns {boolean}
 */
export function isCsvFile(fileName) {
  return /\.csv$/i.test(String(fileName || ''));
}

/**
 * Coerce API extraction payload into a stable review shape.
 *
 * @param {Object} raw - Response body or nested `data`
 * @param {string} [sourceFileName]
 * @returns {import('../types/orderConfirmationExtractionTypes').ExtractedOrderConfirmationData}
 */
export function normalizeExtractedOrderConfirmationResponse(raw, sourceFileName = '') {
  const source = raw?.data && typeof raw.data === 'object' ? raw.data : raw || {};
  const supplier = source.supplier && typeof source.supplier === 'object' ? source.supplier : {};
  const order = source.order && typeof source.order === 'object' ? source.order : {};
  const totals = source.totals && typeof source.totals === 'object' ? source.totals : {};
  const items = Array.isArray(source.items) ? source.items : [];
  const warnings = Array.isArray(source.warnings) ? source.warnings : [];
  const meta = source.meta && typeof source.meta === 'object' ? { ...source.meta } : {};

  if (!meta.sourceFileName && sourceFileName) {
    meta.sourceFileName = sourceFileName;
  }
  if (meta.parser_format && !meta.parserFormat) {
    meta.parserFormat = meta.parser_format;
  }

  return {
    supplier: {
      name: supplier.name ?? null,
      sourceType: supplier.sourceType ?? supplier.source_type ?? null,
      recognizedBrand: supplier.recognizedBrand ?? supplier.recognized_brand ?? null,
      materiahSupplierHint:
        supplier.materiahSupplierHint ?? supplier.materiah_supplier_hint ?? null,
    },
    order: {
      orderNumber: order.orderNumber ?? order.order_number ?? null,
      orderDate: order.orderDate ?? order.order_date ?? null,
      currency: order.currency ?? null,
    },
    items: items.map(row => ({
      productNumber: row.productNumber ?? row.product_number ?? null,
      description: row.description ?? null,
      quantity: row.quantity ?? null,
      lineTotal: row.lineTotal ?? row.line_total ?? null,
      currency: row.currency ?? null,
      rawRow: row.rawRow ?? row.raw_row ?? null,
    })),
    totals: {
      itemsSubtotal: totals.itemsSubtotal ?? totals.items_subtotal ?? null,
    },
    warnings: warnings.map(w => ({
      field: w.field,
      message: String(w.message || ''),
      severity: w.severity === 'error' || w.severity === 'info' ? w.severity : 'warning',
    })),
    meta,
  };
}

const EXTRACT_FALLBACK =
  'Could not extract order confirmation data from this CSV.';
const SUBMIT_FALLBACK =
  'Failed to save order confirmation. The submit endpoint may not be available yet.';

/**
 * @param {*} errorData
 * @param {string} fallback
 * @param {number|null} [httpStatus]
 * @returns {{ message: string, lines: string[], status: number|null }}
 */
export function parseOrderConfirmationApiError(
  errorData,
  fallback,
  httpStatus = null
) {
  return parseApiErrorResponse(errorData, fallback, httpStatus);
}

/**
 * Parse Sigma order confirmation CSV in the browser (no backend extract call).
 *
 * @async
 * @param {File} file
 * @returns {Promise<import('../types/orderConfirmationExtractionTypes').ExtractedOrderConfirmationResponse & { message?: string, errorLines?: string[], status?: number|null, details?: * }>}
 */
export async function extractOrderConfirmationCsv(file) {
  if (!file || !isCsvFile(file.name)) {
    return {
      success: false,
      error: 'Invalid file type',
      message: 'Only CSV files are supported.',
    };
  }
  if (file.size > MAX_CSV_BYTES) {
    return {
      success: false,
      error: 'File too large',
      message: 'File size must be less than 5MB.',
    };
  }

  try {
    logInfo(LOG_CONTEXT, 'extractOrderConfirmationCsv start', {
      fileName: file.name,
      fileSize: file.size,
    });

    const csvText = await readCsvFileAsText(file);
    const parsed = parseSigmaAldrichOrderConfirmationCsv(csvText, file.name);

    if (!parsed.success || !parsed.data) {
      const message = parsed.message || parsed.error || EXTRACT_FALLBACK;
      logError(LOG_CONTEXT, 'extractOrderConfirmationCsv parse failed', {
        fileName: file.name,
        message,
      });
      return {
        success: false,
        error: message,
        message,
        errorLines: [message],
      };
    }

    const data = normalizeExtractedOrderConfirmationResponse(parsed.data, file.name);

    logOrderConfirmationExtraction(
      LOG_CONTEXT,
      'extractOrderConfirmationCsv success — normalized review draft',
      data,
      { fileName: file.name }
    );

    return { success: true, data };
  } catch (error) {
    const message = error?.message || EXTRACT_FALLBACK;
    logError(LOG_CONTEXT, 'extractOrderConfirmationCsv failed', {
      fileName: file.name,
      message,
    });
    return {
      success: false,
      error: message,
      message,
      errorLines: [message],
    };
  }
}

/**
 * Build multipart FormData for submit-reviewed (Excel-parity: file + JSON payload).
 *
 * @param {File} csvFile
 * @param {import('../types/orderConfirmationExtractionTypes').SubmitReviewedOrderConfirmationPayload} payload
 * @returns {{ formData: FormData, fileWithId: { id: string, file: File } }}
 */
export function buildOrderConfirmationSubmitFormData(csvFile, payload) {
  const fileWithId = {
    id: `csv_${Date.now()}_0`,
    file: csvFile,
  };
  const formData = new FormData();
  formData.append('files', fileWithId.file);
  formData.append('file_ids', fileWithId.id);
  formData.append('payload', JSON.stringify(payload));
  return { formData, fileWithId };
}

/**
 * Backend finished CSV upload during multipart submit (dual quote + order PO).
 * When true, skip presigned POST and update_quote_upload_status finalize.
 *
 * @param {Object} [data] - submit-reviewed response body
 * @returns {boolean}
 */
export function orderConfirmationCsvUploadsCompletedOnServer(data) {
  return (
    data?.csv_uploads_completed === true ||
    data?.csvUploadsCompleted === true
  );
}

/**
 * Whether the client should upload CSV to S3 and call finalize after submit-reviewed.
 *
 * @param {Object} [data] - submit-reviewed response body
 * @returns {boolean}
 */
export function orderConfirmationNeedsClientCsvUpload(data) {
  if (orderConfirmationCsvUploadsCompletedOnServer(data)) {
    return false;
  }
  const presignedUrls = data?.presigned_urls;
  return Array.isArray(presignedUrls) && presignedUrls.length > 0;
}

/**
 * Upload CSV to S3 via presigned URLs and finalize quote upload status.
 *
 * @async
 * @param {string} token
 * @param {Array} presignedUrls
 * @param {{ id: string, file: File }} fileWithId
 * @returns {Promise<{ success: boolean, error?: string, message?: string }>}
 */
async function uploadOrderConfirmationCsvToS3AndFinalize(
  token,
  presignedUrls,
  fileWithId
) {
  const uploadResult = await uploadQuoteExcelsToS3(presignedUrls, [fileWithId]);
  if (!uploadResult?.uploadStatuses) {
    logError(LOG_CONTEXT, 'submitReviewedOrderConfirmation S3 upload failed', {
      fileName: fileWithId.file?.name,
    });
    return {
      success: false,
      error: 'Failed to upload order confirmation CSV to storage.',
      message: 'Failed to upload order confirmation CSV to storage.',
    };
  }

  const finalizeResult = await finalizeQuotePdfUploadStatus(
    token,
    uploadResult.uploadStatuses
  );
  if (finalizeResult && !finalizeResult.success) {
    logError(LOG_CONTEXT, 'submitReviewedOrderConfirmation finalize failed', {
      fileName: fileWithId.file?.name,
      finalizeError: finalizeResult.error,
    });
    return {
      success: false,
      error:
        finalizeResult.messages?.join('\n') ||
        'Quote and order were created but file upload could not be finalized.',
      message:
        finalizeResult.messages?.join('\n') ||
        'Quote and order were created but file upload could not be finalized.',
    };
  }

  return { success: true };
}

/**
 * Submit user-reviewed order confirmation → quote + order (sent_to_supplier).
 * Sends the original CSV via multipart (files + file_ids + payload JSON), then
 * uploads to S3 when the backend returns presigned_urls (same pattern as Excel).
 *
 * @async
 * @param {string} token
 * @param {import('../types/orderConfirmationExtractionTypes').SubmitReviewedOrderConfirmationPayload} payload
 * @param {File} csvFile - Original Sigma CSV selected by the user (required).
 * @returns {Promise<import('../types/orderConfirmationExtractionTypes').SubmitReviewedOrderConfirmationResult>}
 */
export async function submitReviewedOrderConfirmation(token, payload, csvFile) {
  if (!csvFile) {
    const message = 'Order confirmation CSV file is missing. Select the file again and retry.';
    logError(LOG_CONTEXT, 'submitReviewedOrderConfirmation missing csvFile', {});
    return {
      success: false,
      error: message,
      message,
      errorLines: [message],
    };
  }

  logInfo(LOG_CONTEXT, 'submitReviewedOrderConfirmation start', {
    supplier: payload?.supplier,
    itemCount: payload?.quote_items?.length ?? 0,
    orderNumber: payload?.order_confirmation?.order_number ?? null,
    hasFundingCredit: payload?.funding_credit_id != null,
    fundingAmount: payload?.funding_amount ?? null,
    fileName: csvFile.name,
    fileSize: csvFile.size,
  });

  const { formData, fileWithId } = buildOrderConfirmationSubmitFormData(
    csvFile,
    payload
  );

  try {
    const response = await axios.post(
      `${BACKEND_URL}order-confirmations/submit-reviewed/`,
      formData,
      {
        headers: {
          Authorization: `Token ${token}`,
          'Content-Type': 'multipart/form-data',
        },
        timeout: 300000,
        maxContentLength: Infinity,
        maxBodyLength: Infinity,
      }
    );

    const data = response?.data || {};
    const apiSuccess = Boolean(data.success !== false);
    let uploadError = null;

    if (orderConfirmationCsvUploadsCompletedOnServer(data)) {
      logInfo(
        LOG_CONTEXT,
        'submitReviewedOrderConfirmation server-side CSV upload already completed — skipping client S3/finalize',
        {
          quoteId: data.quote_id ?? data.quoteId,
          orderId: data.order_id ?? data.orderId,
          csv_uploads_completed: data.csv_uploads_completed ?? data.csvUploadsCompleted,
        }
      );
    } else if (orderConfirmationNeedsClientCsvUpload(data)) {
      const presignedUrls = data.presigned_urls;
      const uploadOutcome = await uploadOrderConfirmationCsvToS3AndFinalize(
        token,
        presignedUrls,
        fileWithId
      );
      if (!uploadOutcome.success) {
        uploadError = uploadOutcome.message || uploadOutcome.error;
      }
    }

    if (!apiSuccess) {
      const parsed = parseOrderConfirmationApiError(data, SUBMIT_FALLBACK);
      logError(LOG_CONTEXT, 'submitReviewedOrderConfirmation rejected', {
        message: parsed.message,
        errorLines: parsed.lines,
        responseBody: data,
      });
      return {
        success: false,
        error: parsed.message,
        message: parsed.message,
        errorLines: parsed.lines,
        details: data,
      };
    }

    if (uploadError) {
      logError(LOG_CONTEXT, 'submitReviewedOrderConfirmation upload failed after create', {
        message: uploadError,
        quoteId: data.quote_id ?? data.quoteId,
        orderId: data.order_id ?? data.orderId,
      });
      return {
        success: false,
        error: uploadError,
        message: uploadError,
        errorLines: [uploadError],
        quoteId: data.quote_id ?? data.quoteId ?? null,
        orderId: data.order_id ?? data.orderId ?? null,
        warnings: Array.isArray(data.warnings) ? data.warnings : [],
        details: data,
      };
    }

    const normalized = {
      success: true,
      quoteId: data.quote_id ?? data.quoteId ?? null,
      orderId: data.order_id ?? data.orderId ?? null,
      message: data.message || '',
      warnings: Array.isArray(data.warnings) ? data.warnings : [],
      raw: data,
    };

    logInfo(LOG_CONTEXT, 'submitReviewedOrderConfirmation succeeded', {
      quoteId: normalized.quoteId,
      orderId: normalized.orderId,
      csvUploadsCompletedOnServer: orderConfirmationCsvUploadsCompletedOnServer(data),
      hadPresignedUrls: orderConfirmationNeedsClientCsvUpload(data),
    });

    return normalized;
  } catch (error) {
    const status = error.response?.status ?? null;
    const errorData = error.response?.data;
    const parsed = parseOrderConfirmationApiError(
      errorData,
      SUBMIT_FALLBACK,
      status
    );
    logError(LOG_CONTEXT, 'submitReviewedOrderConfirmation failed', {
      message: parsed.message,
      errorLines: parsed.lines,
      status,
      responseBody: errorData ?? error.message,
    });
    return {
      success: false,
      error: parsed.message,
      message: parsed.message,
      errorLines: parsed.lines,
      status,
      details: errorData,
    };
  }
}
